import * as React from 'react';
import { IData } from '../virtual-data/VirtualData';
import { FinalTableComputedColumn, FinalTableProps } from './FinalTableProps';
import { GetRowPropsType } from '../table/TableProps';
import { CheckIdsProps } from '../rows-data/useCheckIds';
import { TableRowsHeights } from './utils';
import { GridCellProps } from 'react-virtualized/dist/es/Grid';
import { ReactNode, useState } from 'react';
import { BodyCell } from './BodyCell';
import classNames from 'classnames';
import { Link } from '../link/Link';
import * as Classes from '../common/classes';

type Props<DATA extends IData<ID>, FILTER, ID> = {
  finalTableProps: FinalTableProps<DATA, FILTER, ID>;
  formattedColumns: FinalTableComputedColumn<DATA>[];
  checkIds: CheckIdsProps<ID>;
  heights: TableRowsHeights;
  gridCellProps: GridCellProps;
  columnsWidth: { [key: number]: number };
  rowDisableHover: boolean;
  getRowProps?: GetRowPropsType<DATA>;
  getGroupRowProps?: GetRowPropsType<DATA>;
  highlightId?: ID;
  highlightRowIndex?: number;
  showKebabOnHover?: boolean;
  areaWidth?: number;
  scrollLeft?: number;
  columnWidthSum?: number;
};

export const BodyRow = <DATA extends IData<ID>, FILTER, ID>({
  finalTableProps,
  formattedColumns,
  checkIds,
  heights,
  gridCellProps,
  columnsWidth,
  rowDisableHover,
  areaWidth,
  getRowProps,
  getGroupRowProps,
  highlightId,
  highlightRowIndex,
  columnWidthSum = 0,
  scrollLeft = 0,
}: Props<DATA, FILTER, ID>) => {
  const isGroupRow = finalTableProps.isGroupByIndex(gridCellProps.rowIndex);
  const [isHovered, setIsHovered] = useState(false);

  const cells: ReactNode[] = [];

  let x = 0;
  let actionButton: React.ReactNode | null = null;

  let rowRenderProps = finalTableProps.getRowRenderProps(
    gridCellProps.rowIndex
  );
  const highlight =
    highlightId === rowRenderProps.id ||
    highlightRowIndex === gridCellProps.rowIndex;

  let showFloatingKebab = false;

  for (let colIndex = 0; colIndex < formattedColumns.length; colIndex++) {
    const cellStyle: React.CSSProperties = {
      position: 'absolute',
      height: isGroupRow
        ? heights.groupRowHeight
        : highlight
          ? heights.rowHeight - 1
          : heights.rowHeight,
      // Offset o 1px prvního a posledního sloupce pro zvýrazněný řádek
      paddingRight: colIndex === 0 && highlight ? 1 : undefined,
      paddingLeft:
        colIndex === formattedColumns.length - 1 && highlight
          ? 1 +
            (formattedColumns[formattedColumns.length - 1].type === 'action'
              ? 6
              : 1)
          : undefined,
      width: columnsWidth[colIndex],
      left: x,
      top: 0,
      // Pro viditelnost highlightu prvního řádku
      zIndex: highlight && gridCellProps.rowIndex === 0 ? 1 : undefined,
    };
    x += columnsWidth[colIndex];

    const bodyGridCellProps: GridCellProps = {
      rowIndex: gridCellProps.rowIndex,
      columnIndex: colIndex,
      key: `${gridCellProps.key}_${gridCellProps.rowIndex}_${colIndex}`,
      isScrolling: false,
      isVisible: true,
      parent: gridCellProps.parent,
      style: cellStyle,
    };
    const column = formattedColumns[colIndex];
    if (column.type === 'action') {
      if (
        areaWidth !== undefined &&
        bodyGridCellProps.style &&
        bodyGridCellProps.style.left !== undefined &&
        bodyGridCellProps.style.width !== undefined
      ) {
        showFloatingKebab =
          scrollLeft + areaWidth <
          // @ts-ignore
          bodyGridCellProps.style.left + bodyGridCellProps.style.width - 14 - 4;
      }
    }
    cells.push(
      <BodyCell
        key={colIndex}
        finalTableProps={finalTableProps}
        column={{
          ...column,
          className: classNames(
            column.className,
            column.type === 'action' && showFloatingKebab
              ? Classes.FINAL_TABLE_ROW_KEBAB_SHOWN
              : undefined
          ),
        }}
        gridCellProps={bodyGridCellProps}
        isHovered={isHovered}
        setIsHover={(isHovered) => setIsHovered(isHovered)}
      />
    );
    if (column.type === 'action') {
      const rowData = finalTableProps.getRowByIndex(gridCellProps.rowIndex);
      if (showFloatingKebab && rowData !== undefined && rowData !== null) {
        actionButton = (
          <div
            className={Classes.FINAL_TABLE_ROW_KEBAB}
            style={{
              right: columnWidthSum - (areaWidth ?? 0) - scrollLeft,
              height: heights.rowHeight + 1,
            }}
          >
            {column.cell?.({
              rowIndex: bodyGridCellProps.rowIndex,
              columnIndex: bodyGridCellProps.columnIndex,
              // @ts-ignore
              row: rowData,
              column: {
                ...column,
              },
            })}
          </div>
        );
      }
    }
  }

  rowRenderProps.checked = checkIds.isChecked(rowRenderProps.id);
  rowRenderProps.highlight = highlight;

  let rowPropsInput = {
    row: rowRenderProps.row,
    rowIndex: gridCellProps.rowIndex,
  };
  const customRowProps = isGroupRow
    ? getGroupRowProps?.(rowPropsInput)
    : getRowProps?.(rowPropsInput);

  const itemClassName = classNames(
    Classes.FINAL_TABLE_ROW,
    Classes.HOVER_CONTAINER,
    customRowProps?.className,
    {
      [Classes.HOVER_ITEM_CONTAINER]: !rowDisableHover,
      [Classes.CLICK_CONTAINER]:
        customRowProps?.onClick || customRowProps?.link,
      [Classes.CHECKED_CONTAINER]: rowRenderProps.checked,
      [Classes.HIGHLIGHT_CONTAINER]: rowRenderProps.highlight,
      [Classes.FINAL_TABLE_GROUP_ROW]: isGroupRow,
    }
  );
  const itemStyle = {
    ...customRowProps?.style,
    ...gridCellProps.style,
  };

  // Kvůli mezeře uskupin musíme dělat tuhle opičárnu - výška řádku je vlastně groupRowHeight + groupDepth0Spacing a zde musíme výsku snižovat o groupDepth0Spacing a protože mezera má být nahoře, tak top posouvat dolů o groupDepth0Spacing
  if (isGroupRow) {
    itemStyle.height = heights.groupRowHeight;
    // @ts-ignore
    // itemStyle.top += heights.groupDepth0Spacing;
  }

  if (customRowProps?.link && rowRenderProps?.row) {
    return (
      <Link
        key={gridCellProps.rowIndex}
        {...customRowProps.link}
        className={itemClassName}
        style={itemStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {actionButton}
        <>{cells}</>
      </Link>
    );
  } else {
    return (
      <div
        key={gridCellProps.rowIndex}
        className={itemClassName}
        style={itemStyle}
        {...(rowRenderProps?.row && { onClick: customRowProps?.onClick })}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {actionButton}
        {cells}
      </div>
    );
  }
};
